import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useMemo, useRef, useState } from "react";
import { Button } from "react-aria-components";

import dividerIcon from "@/assets/figma/creative-media/divider.svg";
import downloadIcon from "@/assets/figma/creative-media/download.svg";
import editIcon from "@/assets/figma/creative-media/edit.svg";
import fileIcon from "@/assets/figma/creative-media/file.svg";
import type { ConversationCreativeMediaPart } from "@/features/chat/creativeMediaSchemas";
import { useGetCreativeMediaJobQuery } from "@/features/chat/creativeMediaApi";
import { useCreativeMediaAccessUrl } from "@/features/chat/useCreativeMediaAccessUrl";
import { useGetMyFilesResourceAccessUrlMutation } from "@/features/my-files/myFilesApi";

gsap.registerPlugin(useGSAP);

interface ConversationMediaGroupProps {
  conversationId: string;
  onOpenCanvas: (jobId: string, resourceId?: string) => void;
  organizationId: string;
  parts: ReadonlyArray<ConversationCreativeMediaPart>;
  shopBindingId: string;
}

interface MediaGroup {
  jobId: string;
  parts: ConversationCreativeMediaPart[];
}

const singleRestingLayout = [
  { opacity: 0.18, rotate: -13, scale: 0.82, x: -196, y: 14 },
  { opacity: 0.46, rotate: -7, scale: 0.91, x: -102, y: 5 },
  { opacity: 1, rotate: 0, scale: 1, x: 0, y: 0 },
  { opacity: 0.46, rotate: 7, scale: 0.91, x: 102, y: 5 },
  { opacity: 0.18, rotate: 13, scale: 0.82, x: 196, y: 14 },
] as const;

const mosaicRestingLayout = [
  { opacity: 0.22, rotate: -11, scale: 0.84, x: -176, y: 13 },
  { opacity: 0.66, rotate: -5, scale: 0.92, x: -88, y: 5 },
  { opacity: 1, rotate: 0, scale: 1, x: 0, y: 0 },
  { opacity: 0.66, rotate: 5, scale: 0.92, x: 88, y: 5 },
  { opacity: 0.22, rotate: 11, scale: 0.84, x: 176, y: 13 },
] as const;

export function ConversationMediaGroups({
  conversationId,
  onOpenCanvas,
  organizationId,
  parts,
  shopBindingId,
}: ConversationMediaGroupProps) {
  const groups = useMemo(() => groupMediaParts(parts), [parts]);
  return groups.map((group) => (
    <ConversationMediaDeck
      conversationId={conversationId}
      group={group}
      key={group.jobId}
      onOpenCanvas={onOpenCanvas}
      organizationId={organizationId}
      shopBindingId={shopBindingId}
    />
  ));
}

function ConversationMediaDeck({
  conversationId,
  group,
  onOpenCanvas,
  organizationId,
  shopBindingId,
}: {
  conversationId: string;
  group: MediaGroup;
  onOpenCanvas: ConversationMediaGroupProps["onOpenCanvas"];
  organizationId: string;
  shopBindingId: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [downloadAccessURL] = useGetMyFilesResourceAccessUrlMutation();
  const isLegacy = group.jobId.startsWith("legacy:");
  const jobQuery = useGetCreativeMediaJobQuery(
    { conversationId, jobId: group.jobId, organizationId, shopBindingId },
    { skip: isLegacy },
  );
  const visibleParts = group.parts.slice(0, 5);
  const presentation =
    group.parts.some((part) => part.data.presentation === "mosaic") ||
    group.parts.length > 1
      ? "mosaic"
      : "single";
  const activePart =
    activeIndex === null ? null : visibleParts[activeIndex] ?? null;
  const acceptedResourceId =
    jobQuery.data?.selection?.status === "accepted"
      ? jobQuery.data.selection.resourceId
      : "";

  useGSAP(
    () => {
      const root = rootRef.current;
      if (!root) return;
      const cards = [...root.querySelectorAll<HTMLElement>("[data-media-candidate]")];
      const toolbar = root.querySelector<HTMLElement>("[data-media-toolbar]");
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const layout = presentation === "mosaic" ? mosaicRestingLayout : singleRestingLayout;
      cards.forEach((card, index) => {
        const centerOffset = Math.floor(visibleParts.length / 2);
        const normalizedIndex = index - centerOffset + 2;
        const resting = layout[Math.max(0, Math.min(4, normalizedIndex))] ?? layout[2];
        const active = activeIndex === index;
        const target = activeIndex === null
          ? resting
          : active
            ? { opacity: 1, rotate: 0, scale: 1.1, x: 0, y: -10 }
            : { ...resting, opacity: Math.min(resting.opacity, 0.2), scale: resting.scale * 0.9, y: resting.y + 12 };
        gsap.to(card, {
          autoAlpha: target.opacity,
          duration: reduced ? 0 : 0.34,
          ease: "power3.out",
          overwrite: "auto",
          rotate: target.rotate,
          scale: target.scale,
          x: target.x,
          y: target.y,
          zIndex: active ? 20 : index + 1,
        });
      });
      if (toolbar) {
        gsap.to(toolbar, {
          autoAlpha: activePart ? 1 : 0,
          duration: reduced ? 0 : 0.22,
          ease: "power2.out",
          overwrite: "auto",
          pointerEvents: activePart ? "auto" : "none",
          y: activePart ? 0 : -6,
        });
      }
      return () => {
        gsap.killTweensOf(cards);
        if (toolbar) gsap.killTweensOf(toolbar);
      };
    },
    {
      dependencies: [activeIndex, activePart, presentation, visibleParts.length],
      scope: rootRef,
    },
  );

  const downloadActive = async () => {
    if (!activePart) return;
    const result = await downloadAccessURL({
      download: true,
      organizationId,
      resourceId: activePart.data.resource_id,
    }).unwrap();
    const anchor = document.createElement("a");
    anchor.href = result.url;
    anchor.rel = "noopener";
    anchor.download = "";
    anchor.click();
  };

  return (
    <div
      className="conversation-media-deck"
      data-accepted={acceptedResourceId ? "true" : "false"}
      data-figma-source-node-ids={
        presentation === "mosaic" ? "645:99349,645:99891" : "644:94728,644:94965"
      }
      data-presentation={presentation}
      onMouseLeave={() => setActiveIndex(null)}
      ref={rootRef}
    >
      <div aria-label="Merry 生成的媒体候选" className="conversation-media-deck__stage">
        {visibleParts.map((part, index) => (
          <MediaCandidateCard
            accepted={acceptedResourceId === part.data.resource_id}
            index={index}
            key={`${part.data.job_id}:${part.data.resource_id}:${part.data.role}`}
            onActivate={() => setActiveIndex(index)}
            onOpen={isLegacy ? undefined : () => onOpenCanvas(group.jobId, part.data.resource_id)}
            organizationId={organizationId}
            part={part}
          />
        ))}
      </div>

      <div
        aria-hidden={activePart ? undefined : true}
        aria-label="媒体候选操作"
        className="conversation-media-deck__toolbar"
        data-media-toolbar=""
        role="toolbar"
      >
        <Button
          aria-label="打开画布编辑"
          className="conversation-media-deck__tool"
          isDisabled={isLegacy}
          onPress={() => activePart && onOpenCanvas(group.jobId, activePart.data.resource_id)}
        >
          <img alt="" aria-hidden="true" src={editIcon} />
          <span>编辑图组</span>
        </Button>
        <img alt="" aria-hidden="true" className="conversation-media-deck__divider" src={dividerIcon} />
        <Button
          aria-label="下载当前媒体"
          className="conversation-media-deck__tool conversation-media-deck__tool--icon"
          onPress={() => void downloadActive()}
        >
          <img alt="" aria-hidden="true" src={downloadIcon} />
        </Button>
      </div>
    </div>
  );
}

function MediaCandidateCard({
  accepted,
  index,
  onActivate,
  onOpen,
  organizationId,
  part,
}: {
  accepted: boolean;
  index: number;
  onActivate: () => void;
  onOpen?: () => void;
  organizationId: string;
  part: ConversationCreativeMediaPart;
}) {
  const access = useCreativeMediaAccessUrl(organizationId, part.data.resource_id);
  return (
    <Button
      aria-label={`${part.data.media_kind === "video" ? "视频" : "图片"}候选 ${index + 1}${accepted ? "，已采纳" : ""}`}
      className="conversation-media-candidate"
      data-accepted={accepted ? "true" : "false"}
      data-media-candidate=""
      onFocus={onActivate}
      onHoverChange={(hovered) => hovered && onActivate()}
      onPress={access.error ? access.retry : onOpen}
    >
      {access.loading ? (
        <span className="conversation-media-candidate__loading" />
      ) : access.error ? (
        <span className="conversation-media-candidate__error">
          <img alt="" aria-hidden="true" src={fileIcon} />
          <span>{access.error}</span>
          <small>点击重试</small>
        </span>
      ) : part.data.media_kind === "video" ? (
        <video aria-hidden="true" muted playsInline preload="metadata" src={access.url} />
      ) : (
        <img alt="" aria-hidden="true" draggable="false" src={access.url} />
      )}
      {accepted ? <span className="conversation-media-candidate__accepted">已采纳</span> : null}
    </Button>
  );
}

function groupMediaParts(parts: ReadonlyArray<ConversationCreativeMediaPart>) {
  const groups = new Map<string, ConversationCreativeMediaPart[]>();
  for (const part of parts) {
    const current = groups.get(part.data.job_id) ?? [];
    current.push(part);
    groups.set(part.data.job_id, current);
  }
  return [...groups.entries()].map(([jobId, candidates]) => ({
    jobId,
    parts: candidates
      .filter((part, index, values) =>
        values.findIndex((candidate) =>
          candidate.data.resource_id === part.data.resource_id &&
          candidate.data.role === part.data.role,
        ) === index)
      .sort((left, right) => left.data.ordinal - right.data.ordinal),
  }));
}
